import { SupabaseClient } from "@supabase/supabase-js";
import { ProductCode } from "@/app/types/productCodes";
import { PaymentTier } from "@/app/types/payment";
import { WorkDb } from "../workDb";

/**
 * Fetch a single product code by its identifier.
 *
 * @param client - Supabase client
 * @param code - UUID of the product code to fetch
 * @returns The product code record or null if it doesn't exist
 */
export async function getProductCode(
  client: SupabaseClient,
  code: string,
): Promise<ProductCode | null> {
  const { data, error } = await client
    .from("product_codes")
    .select("*")
    .ilike("code", code)
    .maybeSingle();

  if (error) {
    throw new Error(`Error fetching product code: ${error.message}`);
  }

  if (!data) {
    return null;
  }

  const productTier = data.tier_product_id
    ? await WorkDb.getProductById(data.tier_product_id)
    : null;

  const productCode: ProductCode = {
    ...data,
    tier: (productTier?.code_name ?? null) as PaymentTier | null,
    product: productTier,
  };

  return productCode;
}

/**
 * Mark a product code as used.
 *
 * @param client - Supabase client
 * @param code - UUID of the product code to redeem
 * @param userId - ID of the user redeeming the code
 * @returns The updated product code record or null if it doesn't exist
 */
export async function redeemProductCode(
  client: SupabaseClient,
  code: string,
  userId: string,
): Promise<ProductCode | null> {
  const { error } = await client
    .from("product_codes")
    .update({
      status: "used", // active, used, expired
      used_by: userId,
      used_at: new Date().toISOString(),
    })
    .eq("code", code)
    .select();

  if (error) {
    throw new Error(`Error redeeming product code: ${error.message}`);
  }

  return getProductCode(client, code);
}
